import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans } from 'react-i18next';
import { Button, SquareButton, Chip, NotFound, Input } from 'components';
import { ButtonVariant, Link } from '@app/core';
import { useIsAdmin } from 'queries';
import { SettingContent } from './components/SettingContent';

type SquareVariants = 'muted' | 'plain' | 'transparent';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h2,
    marginBottom: theme.marginBase * 2,
  },
  title2: {
    ...theme.fonts.h3,
    marginTop: theme.marginBase * 3,
  },
  title3: {
    ...theme.fonts.h4,
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
  },
  buttonList: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.marginBase,
    },
  },
  grayBg: {
    backgroundColor: theme.colors.grayBackground,
  },
  rightAction: {
    ...theme.fonts.caption,
    color: theme.colors.blueAccessible,
  },
  questionNumberContainer: {
    backgroundColor: theme.colors.blueMuted,
    borderRadius: theme.borderRadius.std,
    minHeight: theme.marginBase * 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.marginBase,
  },
  questionNumber: {
    textAlign: 'center',
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
}));

const buttonVariants: ButtonVariant[] = ['plain', 'transparent', 'muted'];
const squareVariant: SquareVariants[] = ['muted', 'transparent', 'plain'];

export const UIKit = () => {
  const theme: Theme = useTheme() as Theme;
  const classes = useStyles({ theme });
  const isAdmin = useIsAdmin();

  const colors = theme.colorList;
  if (!isAdmin) {
    return <NotFound />;
  }

  return (
    <SettingContent>
      <h1 className={classes.title}>
        <Trans i18nKey="settings.uikit.title" />
      </h1>
      <h2 className={classes.title2}>Buttons</h2>
      {buttonVariants.map((variant) => (
        <div key={`button_${variant}`}>
          <h3 className={classes.title3}>{variant}</h3>
          <div className={classes.buttonList}>
            {colors.map((color) => (
              <Button
                key={`button_${variant}_${color}`}
                color={color}
                variant={variant}
              >
                Button - {color}
              </Button>
            ))}
          </div>
        </div>
      ))}
      <div className={classes.grayBg}>
        <h3 className={classes.title3}>Muted on gray</h3>
        <div className={classes.buttonList}>
          {colors.map((color) => (
            <Button
              key={`button_light_on_gray_${color}`}
              color={color}
              variant="muted"
              overGray
            >
              Button - {color}
            </Button>
          ))}
        </div>
      </div>
      <div>
        <h3 className={classes.title3}>small</h3>
        <div className={classes.buttonList}>
          {colors.map((color) => (
            <Button key={`button_small_gray_${color}`} color={color}>
              Button - {color}
            </Button>
          ))}
        </div>
      </div>
      <h2 className={classes.title2}>Square Buttons</h2>
      {squareVariant.map((variant) => (
        <div key={`square_button_${variant}`}>
          <h3 className={classes.title3}>{variant}</h3>
          <div className={classes.buttonList}>
            {colors.map((color) => (
              <SquareButton
                key={`square_button_${variant}_${color}`}
                color={color}
                variant={variant}
                icon="ri-eye-line"
              />
            ))}
          </div>
        </div>
      ))}
      <div className={classes.grayBg}>
        <h3 className={classes.title3}>Muted on gray</h3>
        <div className={classes.buttonList}>
          {colors.map((color) => (
            <SquareButton
              key={`square_button_light_on_gray_${color}`}
              color={color}
              variant="muted"
              overGray
              icon="ri-eye-line"
            />
          ))}
        </div>
      </div>
      <h2 className={classes.title2}>Chips</h2>
      <div>
        <h3 className={classes.title3}>default</h3>
        <div className={classes.buttonList}>
          {colors.map((color) => (
            <Chip key={`chip_${color}`} color={color}>
              chip - {color}
            </Chip>
          ))}
        </div>
      </div>
      <div className={classes.grayBg}>
        <h3 className={classes.title3}>Over Gray</h3>
        <div className={classes.buttonList}>
          {colors.map((color) => (
            <Chip key={`chip_over_gray_${color}`} color={color} overGray>
              chip - {color}
            </Chip>
          ))}
        </div>
      </div>

      <h2 className={classes.title2}>Inputs</h2>
      <div>
        <h3 className={classes.title3}>Text</h3>
        <div className={classes.buttonList}>
          <Input label="Text Input" placeholder="Type something..." />
          <Input
            label="Text Input"
            placeholder="Type something..."
            secondaryLabel="With a secondaryLabel"
          />
          <Input
            label="Text Input"
            placeholder="Type something..."
            rightAction={
              <Link to="#" className={classes.rightAction}>
                With a link
              </Link>
            }
          />
          <Input
            label="With insideRightIcon"
            placeholder="Type something..."
            insideRightIcon="ri-eye-line"
          />
          <Input
            label="With hoverRightIcon"
            placeholder="Type something..."
            hoverRightIcon={<i className="ri-delete-bin-line" />}
          />
          <Input
            label="With preFieldComponent"
            placeholder="Type something..."
            small
            preFieldComponent={
              <div className={classes.questionNumberContainer}>
                <span className={classes.questionNumber}>10</span>{' '}
              </div>
            }
          />

          <Input
            label="Required with error"
            placeholder="Type something..."
            requiredStar
            error="The error"
          />
          <Input
            label="Required with warn"
            placeholder="Type something..."
            requiredStar
            warn="The warning"
          />
          <Input
            label="Is a preview"
            placeholder="Type something..."
            preview
            disabled
          />
        </div>
      </div>
    </SettingContent>
  );
};
